import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Send, ChevronLeft, MoreVertical } from "lucide-react";
import { logger } from "../../utils/logger";
import { scormService } from "../../utils/scormService";
import { ChatMessageContent } from "./ChatMessageContent";
import { OtpNotification } from "./OtpNotification";
import { FakeLandingPage } from "./FakeLandingPage";
import type { LandingPage, LandingPageSubmission } from "./FakeLandingPage";
import { SmishingInputHelper } from "./SmishingInputHelper";
import { buildSmishingInitRequest, buildSmishingMessageRequest, type SmishingChatInitResponse, type SmishingChatReplyResponse, type SmishingRequestCategory } from "./smishing-chat-api";

type TelegramStatus = "idle" | "sending" | "receiving" | "ending" | "error" | "finished";

interface TelegramMessage {
  role: "user" | "assistant";
  content: string;
  timestamp?: string;
}

interface TelegramChatProps {
  microlearningId: string;
  language?: string;
  endpoint?: string;
  senderName?: string;
  senderNumber?: string;
  startLabel?: string;
  statusTexts?: Partial<Record<TelegramStatus, string>>;
  linkWarningTitle?: string;
  linkWarningBody?: string;
  otpTexts?: { codeLabel?: string; codeSuffix?: string; backLabel?: string; backHint?: string };
  landingPageTexts?: { backLabel?: string };
  uiTexts?: {
    now?: string;
    inputPlaceholder?: string;
    online?: string;
    typing?: string;
    unknownSender?: string;
    startChat?: string;
    chatEnded?: string;
    errorGeneric?: string;
    errorStartChat?: string;
    errorGetReply?: string;
  };
  embedded?: boolean;
  className?: string;
  onStatusChange?: (status: TelegramStatus) => void;
  onCompletionChange?: (completed: boolean) => void;
}

const FALLBACK_SMISHING_ENDPOINT = "https://agentic-ai-agent.keepnetlabs.com/smishing/chat";
const DEFAULT_SMISHING_ENDPOINT = typeof window !== "undefined" && (
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1"
) ? "http://localhost:4111/smishing/chat" : FALLBACK_SMISHING_ENDPOINT;

const getAccessToken = (): string | null => {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get("accessToken");
};

const formatTime = () => {
  const now = new Date();
  return now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

// Telegram typing animation - shown as a bubble like a message
function TypingBubble() {
  return (
    <div className="flex justify-start">
      <div className="rounded-lg bg-white px-2.5 py-2 shadow-sm">
        <div className="flex items-center gap-1">
          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#A0ACB6] [animation-delay:0ms]" />
          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#A0ACB6] [animation-delay:150ms]" />
          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#A0ACB6] [animation-delay:300ms]" />
        </div>
      </div>
    </div>
  );
}

// Telegram paper plane logo
function TelegramLogo({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="white">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
    </svg>
  );
}

export function TelegramChat({
  microlearningId,
  language = "en-gb",
  endpoint = DEFAULT_SMISHING_ENDPOINT,
  senderName,
  statusTexts,
  linkWarningTitle,
  linkWarningBody,
  otpTexts,
  landingPageTexts,
  uiTexts,
  embedded = false,
  className = "",
  onStatusChange,
  onCompletionChange
}: TelegramChatProps) {
  const [status, setStatus] = useState<TelegramStatus>("idle");
  const [messages, setMessages] = useState<TelegramMessage[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [hasStarted, setHasStarted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [otpData, setOtpData] = useState<{ code: string; sender: string } | null>(null);
  const [expectedResponseCategory, setExpectedResponseCategory] = useState<SmishingRequestCategory | undefined>(undefined);
  const shownOtpCodes = useRef<Set<string>>(new Set());
  const activeOtpRef = useRef<{ code: string; sender: string } | null>(null);
  const [activeLandingPage, setActiveLandingPage] = useState<LandingPage | null>(null);
  const landingPageDataRef = useRef<LandingPage | null>(null);
  const pendingLandingPageRef = useRef<LandingPageSubmission | null>(null);
  const clickedLinkRef = useRef<string | null>(null);
  const autoSendRef = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const initRequestedRef = useRef(false);

  // Get user info from SCORM LMS
  const studentInfo = useMemo(() => scormService.getStudentInfo(), []);
  const resolvedSenderName = senderName || uiTexts?.unknownSender || "Unknown";
  const userName = studentInfo.name || "You";
  const senderInitial = resolvedSenderName.charAt(0).toUpperCase();

  const updateStatus = useCallback((next: TelegramStatus) => {
    setStatus(next);
    onStatusChange?.(next);
  }, [onStatusChange]);

  const statusLabel = useMemo(() => {
    const defaultLabels: Record<TelegramStatus, string> = {
      idle: uiTexts?.startChat || "Tap to start",
      sending: "Sending\u2026",
      receiving: "typing\u2026",
      ending: "Ending\u2026",
      finished: uiTexts?.chatEnded || "Chat ended",
      error: uiTexts?.errorGeneric || "Something went wrong"
    };
    return statusTexts?.[status] ?? statusTexts?.idle ?? defaultLabels[status];
  }, [status, statusTexts, uiTexts]);

  const scrollToBottom = useCallback(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const initChat = useCallback(async () => {
    if (hasStarted || isInitializing) return;
    setIsInitializing(true);
    updateStatus("sending");
    setErrorMessage(null);
    try {
      const token = getAccessToken();
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) headers["X-AGENTIC-ALLY-TOKEN"] = token;

      const response = await fetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify(buildSmishingInitRequest(microlearningId, language, "telegram"))
      });
      const data = await response.json() as SmishingChatInitResponse;
      if (!response.ok || !data.success) {
        throw new Error(data.error || `Init failed: ${response.status}`);
      }
      if (data.firstMessage) {
        setMessages([{ role: "assistant" as const, content: data.firstMessage, timestamp: formatTime() }]);
      }
      // Build-time OTP: show notification shortly after chat opens
      if (data.otpCode && data.otpSender && !shownOtpCodes.current.has(data.otpCode)) {
        shownOtpCodes.current.add(data.otpCode);
        setOtpData({ code: data.otpCode, sender: data.otpSender });
        activeOtpRef.current = { code: data.otpCode, sender: data.otpSender };
      }
      if (data.landingPage) {
        landingPageDataRef.current = data.landingPage;
      }
      if (data.isFinished) {
        setExpectedResponseCategory(undefined);
        setIsFinished(true);
        updateStatus("finished");
        onCompletionChange?.(true);
      } else {
        setExpectedResponseCategory(data.expectedResponseCategory);
        setHasStarted(true);
        updateStatus("idle");
      }
    } catch (error) {
      updateStatus("error");
      setErrorMessage(uiTexts?.errorStartChat || "Failed to start chat.");
      logger.push({
        level: "error",
        code: "TELEGRAM_INIT_FAIL",
        message: "Failed to init telegram chat",
        detail: String(error)
      });
    } finally {
      setIsInitializing(false);
    }
  }, [endpoint, hasStarted, isInitializing, language, microlearningId, onCompletionChange, uiTexts?.errorStartChat, updateStatus]);

  useEffect(() => {
    if (initRequestedRef.current) return;
    initRequestedRef.current = true;
    initChat();
  }, [initChat]);

  const sendMessage = useCallback(async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || status !== "idle" || !hasStarted || isFinished) return;
    const nextMessages: TelegramMessage[] = [...messages, { role: "user" as const, content: trimmed, timestamp: formatTime() }];
    setMessages(nextMessages);
    setInputValue("");
    setExpectedResponseCategory(undefined);
    updateStatus("receiving");
    setErrorMessage(null);

    try {
      const token = getAccessToken();
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) headers["X-AGENTIC-ALLY-TOKEN"] = token;

      const response = await fetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify(buildSmishingMessageRequest({
          microlearningId,
          language,
          channel: "telegram",
          messages: nextMessages,
          pendingOtp: activeOtpRef.current,
          pendingLandingPageSubmission: pendingLandingPageRef.current,
          pendingClickedLink: clickedLinkRef.current,
        }))
      });
      pendingLandingPageRef.current = null;
      clickedLinkRef.current = null;
      const data = await response.json() as SmishingChatReplyResponse;
      if (!response.ok || !data.success) {
        throw new Error(data.error || `Chat failed: ${response.status}`);
      }
      const replyParts = data.replies && data.replies.length > 1 ? data.replies : data.reply ? [data.reply] : [];
      if (replyParts.length > 0) {
        // First message immediately
        let accumulated: TelegramMessage[] = [...nextMessages, { role: "assistant" as const, content: replyParts[0], timestamp: formatTime() }];
        setMessages(accumulated);

        // Subsequent messages with staggered delay (typing effect)
        for (let i = 1; i < replyParts.length; i++) {
          updateStatus("receiving");
          await new Promise(resolve => setTimeout(resolve, 1500 + Math.random() * 1500));
          accumulated = [...accumulated, { role: "assistant" as const, content: replyParts[i], timestamp: formatTime() }];
          setMessages(accumulated);
        }
      }
      // Trigger OTP notification if backend sent one
      if (data.otpCode && data.otpSender && !shownOtpCodes.current.has(data.otpCode)) {
        shownOtpCodes.current.add(data.otpCode);
        setOtpData({ code: data.otpCode, sender: data.otpSender });
        activeOtpRef.current = { code: data.otpCode, sender: data.otpSender };
      }
      // Store landing page data — shown only when user taps the link
      landingPageDataRef.current = data.landingPage || null;
      if (data.isFinished) {
        setExpectedResponseCategory(undefined);
        setIsFinished(true);
        updateStatus("finished");
        onCompletionChange?.(true);
      } else {
        setExpectedResponseCategory(data.expectedResponseCategory);
        updateStatus("idle");
      }
    } catch (error) {
      updateStatus("error");
      setErrorMessage(uiTexts?.errorGetReply || "Failed to get reply.");
      logger.push({
        level: "error",
        code: "TELEGRAM_CHAT_FAIL",
        message: "Failed to fetch telegram reply",
        detail: String(error)
      });
    }
  }, [endpoint, hasStarted, inputValue, isFinished, language, messages, microlearningId, onCompletionChange, status, uiTexts?.errorGetReply, updateStatus]);

  const handleLinkClick = useCallback((url: string): boolean => {
    clickedLinkRef.current = url;
    if (landingPageDataRef.current && landingPageDataRef.current.url === url) {
      setActiveLandingPage(landingPageDataRef.current);
      return true;
    }
    return false;
  }, []);

  const handleLandingPageSubmit = useCallback((submission: LandingPageSubmission) => {
    pendingLandingPageRef.current = submission;
    clickedLinkRef.current = null;
    setActiveLandingPage(null);
    setInputValue(Object.values(submission.fields).join(", "));
    autoSendRef.current = true;
  }, []);

  const handleLandingPageBack = useCallback(() => {
    setActiveLandingPage(null);
  }, []);

  // Auto-send after landing page form submission
  useEffect(() => {
    if (autoSendRef.current && inputValue.trim()) {
      autoSendRef.current = false;
      sendMessage();
    }
  }, [inputValue, sendMessage]);

  const isBusy = status === "sending" || status === "receiving" || isInitializing || isFinished;
  const previewText = messages[0]?.content || statusLabel;
  const wrapperClassName = embedded
    ? `w-full ${className}`
    : `w-full max-w-[420px] glass-border-3 p-4 ${className}`;
  const chatShellClassName = embedded
    ? "flex h-full min-h-0 flex-col overflow-hidden rounded-lg"
    : "flex h-full min-h-[420px] flex-col overflow-hidden rounded-lg";

  return (
    <div className={wrapperClassName}>
      {!isOpen ? (
        <div className="relative flex min-h-[380px] flex-col items-center justify-center">
          {/* Telegram-style notification card */}
          <button
            type="button"
            onClick={() => {
              setIsOpen(true);
              if (!hasStarted) {
                initChat();
              }
            }}
            className="group relative w-full max-w-[270px] overflow-hidden rounded-[18px] border border-white/30 bg-white/80 p-3 text-left shadow-[0_4px_24px_rgba(0,0,0,0.08)] backdrop-blur-xl transition-all duration-200 hover:scale-[1.01] hover:shadow-[0_8px_32px_rgba(0,0,0,0.12)] active:scale-[0.99] dark:border-white/10 dark:bg-[#2C2C2E]/90 dark:shadow-[0_4px_24px_rgba(0,0,0,0.3)]"
          >
            {/* Header: Telegram icon + Telegram + now */}
            <div className="relative flex items-center gap-2">
              {/* Telegram app icon */}
              <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#2AABEE] shadow-sm">
                <TelegramLogo />
              </div>
              <span className="text-[13px] font-semibold text-[#1C1C1E] dark:text-[#F2F2F7]">
                Telegram
              </span>
              <span className="ml-auto text-[12px] text-[#8E8E93] dark:text-[#98989D]">
                {uiTexts?.now || "now"}
              </span>
            </div>

            {/* Sender name & message */}
            <div className="relative mt-1.5 pl-[44px]">
              <div className="flex items-center justify-between">
                <span className="text-[14px] font-semibold text-[#1C1C1E] dark:text-[#F2F2F7]">
                  {resolvedSenderName}
                </span>
                {/* Unread badge */}
                <span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#2AABEE] px-1.5 text-[11px] font-bold text-white">
                  1
                </span>
              </div>
              {/* Message preview */}
              <div className="mt-0.5 pr-3 text-[14px] leading-snug text-[#3C3C43]/70 line-clamp-2 dark:text-[#EBEBF5]/60">
                {previewText}
              </div>
            </div>
          </button>

          {/* Home indicator */}
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2">
            <div className="h-1 w-28 rounded-full bg-black/15 dark:bg-white/20" />
          </div>
        </div>
      ) : (
        <div className={`${chatShellClassName} relative`}>
          {/* OTP Notification overlay */}
          {otpData && (
            <OtpNotification
              otpCode={otpData.code}
              otpSender={otpData.sender}
              codeLabel={otpTexts?.codeLabel}
              codeSuffix={otpTexts?.codeSuffix}
              backLabel={otpTexts?.backLabel}
              backHint={otpTexts?.backHint}
              nowLabel={uiTexts?.now}
            />
          )}
          {activeLandingPage && (
            <FakeLandingPage
              landingPage={activeLandingPage}
              onSubmit={handleLandingPageSubmit}
              onBack={handleLandingPageBack}
              backLabel={landingPageTexts?.backLabel}
            />
          )}
          {/* Telegram header */}
          <div className="shrink-0 flex items-center gap-2 px-2 py-1.5" style={{ backgroundColor: "#517DA2" }}>
            <ChevronLeft size={18} className="shrink-0 text-white/90" />
            {/* Contact avatar */}
            <div className="h-8 w-8 shrink-0 rounded-full bg-gradient-to-br from-[#64B5F6] to-[#1976D2] flex items-center justify-center text-sm font-bold text-white">
              {senderInitial}
            </div>
            {/* Name and status */}
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-medium text-white leading-tight truncate">
                {resolvedSenderName}
              </div>
              <div className="text-[11px] text-white/70 leading-tight">
                {status === "receiving" ? "typing..." : isFinished ? "last seen just now" : "last seen recently"}
              </div>
            </div>
            <MoreVertical size={18} className="shrink-0 text-white/70" />
          </div>

          {/* Chat area - Telegram wallpaper */}
          <div
            ref={scrollRef}
            className="min-h-0 flex-1 overflow-y-auto px-2 py-2"
            style={{ backgroundColor: "#7BA0B2" }}
          >
            {messages.length === 0 && !isInitializing && (
              <div className="flex items-center justify-center py-8 text-xs text-white/80">
                {statusLabel}
              </div>
            )}

            <div className="flex flex-col gap-1">
              {messages.map((msg, idx) => (
                <div
                  key={`${msg.role}-${idx}`}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`relative max-w-[80%] break-words [overflow-wrap:anywhere] rounded-lg px-2.5 pb-1 pt-1.5 shadow-sm ${msg.role === "user"
                        ? "rounded-tr-none bg-[#EFFFDE]"
                        : "rounded-tl-none bg-white"
                      }`}
                  >
                    {/* Sender name for assistant messages */}
                    {msg.role === "assistant" && (
                      <div className="text-[12px] font-medium text-[#3A8DD2] mb-0.5">
                        {resolvedSenderName}
                      </div>
                    )}
                    <p className="text-[13px] leading-snug text-[#000000]">
                      <ChatMessageContent
                        content={msg.content}
                        linkWarningTitle={linkWarningTitle}
                        linkWarningBody={linkWarningBody}
                        onLinkClick={handleLinkClick}
                      />
                    </p>
                    {/* Timestamp + read ticks */}
                    <div className="mt-0.5 flex items-center justify-end gap-0.5">
                      <span className={`text-[9px] ${msg.role === "user" ? "text-[#4FAE4E]" : "text-[#A0ACB6]"}`}>
                        {msg.timestamp}
                      </span>
                      {msg.role === "user" && (
                        <svg viewBox="0 0 16 11" className="h-[10px] w-3.5 text-[#4FAE4E]" fill="currentColor">
                          <path d="M11.071.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.636-2.011-2.175a.463.463 0 0 0-.336-.153.457.457 0 0 0-.336.153.44.44 0 0 0-.142.334.44.44 0 0 0 .142.334l2.39 2.588a.456.456 0 0 0 .685-.012l6.524-8.058a.444.444 0 0 0 .102-.382.44.44 0 0 0-.143-.341z" />
                          <path d="M14.757.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.636-1.2-1.298-.389.483 1.553 1.681a.456.456 0 0 0 .685-.012l6.524-8.058a.444.444 0 0 0-.298-.508z" />
                        </svg>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {status === "receiving" && <TypingBubble />}
            </div>
          </div>

          {/* Input area - Telegram style */}
          <SmishingInputHelper
            category={expectedResponseCategory}
            language={language}
            disabled={isBusy || !hasStarted}
            onSelect={setInputValue}
          />
          <div className="shrink-0 flex items-center gap-1 px-1.5 pb-2 pt-1" style={{ backgroundColor: "#FFFFFF" }}>
            <div className="flex flex-1 items-center rounded-full border border-[#E0E0E0] bg-white px-3 py-1.5">
              <input
                type="text"
                value={inputValue}
                onChange={(event) => setInputValue(event.target.value)}
                onKeyDown={(event) => event.key === "Enter" && sendMessage()}
                placeholder={hasStarted ? (uiTexts?.inputPlaceholder || `Message as ${userName}`) : statusLabel}
                disabled={isBusy || !hasStarted}
                className="flex-1 bg-transparent text-[13px] text-[#000000] placeholder-[#A0ACB6] focus:outline-none"
              />
            </div>
            <button
              type="button"
              onClick={sendMessage}
              disabled={!inputValue.trim() || isBusy || !hasStarted}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#2AABEE] text-white shadow-sm transition-colors disabled:bg-[#2AABEE]/50"
              aria-label="Send message"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}

      {errorMessage && (
        <div className="mt-2 text-xs text-rose-600 dark:text-rose-300">{errorMessage}</div>
      )}
    </div>
  );
}
